import { Injectable, signal } from '@angular/core';

/**
 * TEC-204 — controlled accessibility text scaling.
 *
 * Both platforms let the user enlarge text system-wide, and each reaches the
 * WebView through a different door:
 *
 * - iOS: Ionic sets `html { font: var(--ion-dynamic-font) }` with
 *   `--ion-dynamic-font: -apple-system-body`, so the root font size *is* the
 *   Dynamic Type body size — 17px at the default setting, ~53px at AX5.
 *   Nothing bounds it, and only the rem-based half of the app follows it.
 * - Android: the WebView multiplies every computed font size by the system
 *   `fontScale`, leaving the boxes around the text exactly where they were.
 *   MainActivity now switches that off (`setTextZoom(100)`) and hands us the
 *   raw value instead.
 *
 * We take the preference over: measure it, clamp it, publish it. `accessibility.css`
 * turns the content tier into the root font size, so every `rem` in the app —
 * and in Ionic's own components, rem-based since v7 — follows it. Text and the
 * boxes around it grow together.
 *
 * The clamp is not one number. Device QA on the first cut showed that a single
 * allowance big enough to help body copy is far too much for chrome: navigation
 * labels wrapped out of the tab bar and a wallet balance ran off the screen. So
 * each role gets its own ceiling, largest where legibility is worth the most
 * space and tightest where the layout has none to give.
 */

/** Dynamic Type's `body` size at the default setting — iOS's own baseline. */
const APPLE_BODY_DEFAULT_PX = 17;
/** Root font size everywhere else. */
const DEFAULT_BASE_PX = 16;

/**
 * How much each kind of text may grow. iOS reaches ~3.1x and Android 2x; these
 * are what the layout actually absorbs, measured screen by screen.
 */
export const TEXT_SCALE_CEILINGS = {
  /** Body copy and list content — the text worth enlarging, in the roomiest part of the UI. */
  content: 1.35,
  /** Buttons, tabs, segments: chrome with fixed slots and no room to wrap. */
  ui: 1.2,
  /** Toolbar titles, which share one line with a back button and actions. */
  title: 1.15,
  /** Balances and large figures — already oversized, and they overflow first. */
  display: 1.05,
} as const;

/** Sub-pixel noise floor for the probe measurements. */
const SCALE_EPSILON = 0.02;

/** Marks the probe so we can tell whether the `font` shorthand was honoured. */
const PROBE_FONT_FAMILY = 'tecTextScaleProbe';

declare global {
  interface Window {
    /** Bound by MainActivity; Android only. Read synchronously, no timing window. */
    AndroidTextScale?: { getFontScale(): number };
    /** Fallback push from MainActivity for the window before the binding lands. */
    __osFontScale?: number;
  }
}

@Injectable({ providedIn: 'root' })
export class TextScaleService {
  /** What the user actually asked the OS for, uncapped. */
  private readonly osScaleSignal = signal(1);
  readonly osScale = this.osScaleSignal.asReadonly();

  /** What body copy grows by: `osScale` clamped to the content ceiling. */
  private readonly scaleSignal = signal(1);
  readonly scale = this.scaleSignal.asReadonly();

  private probes: { apple: HTMLElement; adjust: HTMLElement } | null = null;

  initialize(): void {
    if (typeof document === 'undefined') return;

    this.refresh();

    // A font-size change reaches us as a resize, or as a return from the
    // Settings app; on Android MainActivity also fires its own event.
    window.addEventListener('resize', this.refresh);
    window.addEventListener('osfontscalechange', this.refresh);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.refresh();
  };

  private readonly refresh = (): void => {
    const { os, basePx, platformZoom } = this.measure();
    const content = this.clamp(os, TEXT_SCALE_CEILINGS.content);
    const root = document.documentElement;

    this.osScaleSignal.set(os);
    this.scaleSignal.set(content);

    root.style.setProperty('--app-font-base', `${basePx}px`);
    root.style.setProperty('--os-text-scale', `${os}`);
    root.style.setProperty('--app-text-scale', `${content}`);
    // Any zoom the platform is still applying on its own is divided back out
    // in the stylesheet, so the ceilings stay honest about what is rendered
    // even if neutralising it failed.
    root.style.setProperty('--app-text-zoom', `${platformZoom}`);

    // The tighter tiers ride on top of the root as a ratio, since a stylesheet
    // only gets one root font size to work with.
    for (const [role, ceiling] of Object.entries(TEXT_SCALE_CEILINGS)) {
      if (role === 'content') continue;
      root.style.setProperty(`--app-${role}-ratio`, `${this.clamp(os, ceiling) / content}`);
    }

    // Hands the stylesheet the root font size. Set last, so the rule never runs
    // against the placeholder values.
    root.classList.add('app-text-scale-ready');
    // Lets a component tighten its layout only when text is enlarged, without
    // having to know the number.
    root.classList.toggle('app-text-enlarged', content > 1 + SCALE_EPSILON);
  };

  /** Never below 1: shrinking the UI is not something we want to inherit. */
  private clamp(scale: number, ceiling: number): number {
    return Math.min(Math.max(scale, 1), ceiling);
  }

  /**
   * What the OS is asking for, which root font size this platform draws
   * against, and how much text zoom the platform is still applying on its own.
   */
  private measure(): { os: number; basePx: number; platformZoom: number } {
    const probes = this.ensureProbes();
    if (!probes) return { os: 1, basePx: DEFAULT_BASE_PX, platformZoom: 1 };

    // Residual text-only zoom, from the one element that opts back into it.
    // Expected to be 1: iOS has none, and on Android MainActivity switches it
    // off. If it is not, the ceilings still hold — see the division in
    // refresh(), which is what device QA on the first cut needed.
    const platformZoom = this.ratio(probes.adjust, DEFAULT_BASE_PX);

    // `-apple-system-body` resolves to the Dynamic Type body size on iOS and is
    // dropped as invalid everywhere else. Dividing the zoom back out leaves the
    // part the OS text setting is responsible for.
    const appleRatio = this.ratio(probes.apple, APPLE_BODY_DEFAULT_PX) / platformZoom;

    const native = this.androidFontScale();
    const os = native ?? Math.max(appleRatio, platformZoom);

    return {
      os: Math.max(os, 1),
      basePx: this.rendersAppleBodyFont(probes.apple) ? APPLE_BODY_DEFAULT_PX : DEFAULT_BASE_PX,
      platformZoom,
    };
  }

  /**
   * Whether the engine honoured the `font` shorthand, which only Apple's does.
   * A platform test rather than UA sniffing — and it decides nothing but the
   * baseline, so getting it wrong costs 6% of type size, never the scale.
   */
  private rendersAppleBodyFont(probe: HTMLElement): boolean {
    return !getComputedStyle(probe).fontFamily.includes(PROBE_FONT_FAMILY);
  }

  private androidFontScale(): number | null {
    let value: number | undefined;
    try {
      value = window.AndroidTextScale?.getFontScale();
    } catch {
      // Binding present but not callable yet — the pushed value covers it.
    }
    if (value === undefined) value = window.__osFontScale;
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  }

  private ratio(probe: HTMLElement, basePx: number): number {
    const measured = probe.getBoundingClientRect().height / basePx;
    return Number.isFinite(measured) && measured > 1 + SCALE_EPSILON ? measured : 1;
  }

  /**
   * Two off-screen lines of text whose height reports back what the platform
   * did to them. Painted rather than `visibility: hidden` so Android's text
   * autosizer treats them as real content.
   */
  private ensureProbes(): { apple: HTMLElement; adjust: HTMLElement } | null {
    if (this.probes?.apple.isConnected) return this.probes;
    if (!document.body) return null;

    const make = (font: string): HTMLElement => {
      const el = document.createElement('div');
      el.setAttribute('aria-hidden', 'true');
      el.textContent = 'M';
      el.style.cssText = `position:absolute;top:0;left:0;width:320px;opacity:0;pointer-events:none;z-index:-1;${font}`;
      document.body.appendChild(el);
      return el;
    };

    this.probes = {
      // The family and size are the fallbacks for engines that reject the
      // shorthand; where it applies it overrides both, which is how we detect
      // it. `line-height` comes last — the shorthand resets it.
      apple: make(
        `font-family:${PROBE_FONT_FAMILY};font-size:${APPLE_BODY_DEFAULT_PX}px;font:-apple-system-body;line-height:1;`
      ),
      adjust: make(`font-size:${DEFAULT_BASE_PX}px;line-height:1;-webkit-text-size-adjust:auto;text-size-adjust:auto;`),
    };
    return this.probes;
  }
}
